import { STATUS, catLabel } from "@/mock/data";
import { hoyISO, yaPaso } from "@/lib/zona";

const diasEntre = (a, b) => Math.round((new Date(`${b}T12:00:00`) - new Date(`${a}T12:00:00`)) / 864e5);
export const faltaTxt = (fecha) => {
  const n = diasEntre(hoyISO(), fecha);
  return n <= 0 ? "Hoy" : n === 1 ? "Mañana" : `Dentro de ${n} días`;
};
const MESES = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];
export const fechaCorta = (f, anioSiOtro = false) => { const [y, m, d] = f.split("-").map(Number); return `${d} de ${MESES[m - 1]}${anioSiOtro && y !== new Date().getFullYear() ? ` de ${y}` : ""}`; };

const nrm = (v) => (v || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim().split(/\s+/).filter(Boolean);
export const mismoNombre = (a, b) => {
  const x = nrm(a), y = nrm(b);
  return !!x[0] && !!y[0] && (x.join(" ") === y.join(" ") || ((x.length === 1 || y.length === 1) && x[0] === y[0]));
};
// A quién pertenece cada registro. Sin persona explícita (datos anteriores) → titular.
export const personasDe = (familyMembers) => {
  const titular = (familyMembers || []).find((f) => f.es_titular)?.id;
  const conTitular = (id) => (id ? [id] : titular ? [titular] : []);
  const matter = (m) => (m.family_member_id ? [m.family_member_id] : m.vivienda_id || m.vehiculo_id || m.pet_id || m.category === "Ocio" ? [] : conTitular(null));
  const gestion = (g) => (!g.para || g.para === "yo" ? conTitular(null)
    : (familyMembers || []).filter((f) => (g.personas || []).some((n) => mismoNombre(n, f.name))).map((f) => f.id));
  return { matter, gestion, otro: (c) => conTitular(c.family_member_id), titular };
};

const tipoMatter = (m) => (m.category === "Ocio" ? "Plan" : m.tipo === "evento" ? "Cita" : m.amount ? "Pago" : "Vencimiento");
const activo = (m) => ![STATUS.PAST, STATUS.DONE].includes(m.status);

// «Necesita tu atención»: asuntos en atención y seguimientos vencidos. Sin duplicados.
export const itemsAtencion = ({ matters, reminders, familyMembers }) => {
  const hoy = hoyISO();
  const out = [];
  const de = personasDe(familyMembers);
  const porId = Object.fromEntries(matters.map((m) => [m.id, m]));
  matters.filter((m) => m.status === STATUS.ATTENTION).forEach((m) => {
    const f = m.due_date || "";
    const atrasado = f && (f < hoy || (f === hoy && m.tipo !== "evento" && yaPaso(f, m.due_time)) || /^Venció/.test(m.due_label || ""));
    const etiqueta = atrasado ? "Atrasado" : f === hoy ? "Para hoy" : m.tipo === "evento" ? "Cita próxima" : m.amount ? "Pago pendiente" : f ? "Vence pronto" : "Pendiente de revisar";
    out.push({ id: m.id, kind: "matter", m, titulo: m.title, sub: m.due_label, fecha: f || "9999", grupo: atrasado ? 0 : f === hoy ? 1 : 2, etiqueta, ruta: `/asunto/${m.id}`, personas: de.matter(m) });
  });
  const vistos = new Set(out.map((i) => i.id));
  (reminders || []).filter((r) => r.tipo === "seguimiento" && r.status === "Programado" && r.reminder_date && r.reminder_date <= hoy && !vistos.has(r.asunto_id)).forEach((r) => {
    out.push({ id: r.id, kind: "seguimiento", r, titulo: r.text || "Seguimiento", sub: r.reminder_date < hoy ? `Desde el ${fechaCorta(r.reminder_date)}` : "Hoy",
      fecha: r.reminder_date, grupo: r.reminder_date < hoy ? 0 : 1, etiqueta: "Seguimiento pendiente",
      ruta: r.asunto_id ? `/asunto/${r.asunto_id}` : "/correos", personas: r.family_member_id ? [r.family_member_id] : porId[r.asunto_id] ? de.matter(porId[r.asunto_id]) : de.otro({}) });
  });
  return out.sort((a, b) => a.grupo - b.grupo || a.fecha.localeCompare(b.fecha));
};

// «Próximas fechas»: lo que viene después y aún no es urgente.
export const itemsProximas = ({ matters, reminders, gestiones, familyMembers, excluir }) => {
  const hoy = hoyISO();
  const out = [];
  const de = personasDe(familyMembers);
  const porId = Object.fromEntries(matters.map((m) => [m.id, m]));
  matters.filter((m) => activo(m) && m.due_date && m.due_date > hoy && !excluir.has(m.id) && m.status !== STATUS.ATTENTION &&
    (m.status === STATUS.UPCOMING || m.tipo === "evento" || m.avisos_activos)).forEach((m) => {
    out.push({ id: m.id, ruta: m.category === "Ocio" && m.plan_id ? `/ocio?plan=${m.plan_id}` : `/asunto/${m.id}`, titulo: m.title, linea: `${catLabel(m.category)} · ${tipoMatter(m)} · ${fechaCorta(m.due_date, m.category === "Ocio")}`, fecha: m.due_date, hora: m.due_time || "", kind: "matter", personas: de.matter(m) });
  });
  const vistos = new Set([...out.map((i) => i.id), ...excluir]);
  (reminders || []).filter((r) => r.status === "Programado" && r.tipo !== "seguimiento" && r.reminder_date && !vistos.has(r.asunto_id) && !yaPaso(r.reminder_date, r.reminder_time)).forEach((r) => {
    const m = porId[r.asunto_id];
    if (r.asunto_id && (!m || !activo(m))) return;
    vistos.add(r.asunto_id);
    out.push({ id: r.id, ruta: m ? `/asunto/${m.id}` : "/agenda", titulo: m?.title || r.text, linea: `${m ? `${catLabel(m.category)} · ` : ""}Recordatorio · ${fechaCorta(r.reminder_date)}`,
      fecha: r.reminder_date, hora: r.reminder_time, kind: "recordatorio", personas: r.family_member_id ? [r.family_member_id] : m ? de.matter(m) : de.otro({}) });
  });
  (gestiones || []).filter((g) => g.estado === "activa" && !g.en_papelera && g.proxima && g.proxima > hoy).forEach((g) => {
    out.push({ id: g.id, ruta: `/gestiones/${g.id}`, titulo: g.titulo, linea: `${g.importe != null ? "Pago periódico" : "Recordatorio periódico"} · ${fechaCorta(g.proxima)}`, fecha: g.proxima, kind: "periodico", personas: de.gestion(g) });
  });
  return out.sort((a, b) => a.fecha.localeCompare(b.fecha) || (a.hora || "99:99").localeCompare(b.hora || "99:99"));
};
